package components

import androidx.compose.runtime.*
import dev.kilua.compose.ComponentNode
import dev.kilua.core.IComponent
import dev.kilua.html.*
import models.*
import i18n.t

@Composable
fun IComponent.TerritoryCard(
    sectorId: String,
    playerId: String,
    gameState: GameState?,
    selectedCharacterId: String? = null,
    activeTavernDialogue: GameEvent.TavernDialogueReceived? = null,
    activeHunterIntel: GameEvent.HunterIntelReceived? = null,
    onDismissTavernDialogue: () -> Unit = {},
    onDismissHunterIntel: () -> Unit = {},
    onClose: () -> Unit,
    sendAction: (GameAction) -> Unit
) {
    if (gameState == null) return
    
    val territoryDef = MapData[sectorId]
    val territoryState = gameState.territories[sectorId] ?: TerritoryState(
        sectorId = sectorId,
        cultivation = 10,
        protection = territoryDef?.protection ?: 10
    )
    
    val myPlayer = gameState.players.find { it.id == playerId }
    val myCharacters = gameState.characters.filter { it.playerId == playerId }
    val charAtLocation = myCharacters.find { it.currentSector == sectorId && !it.isDead }
    
    val activeChar = myCharacters.find { it.id == selectedCharacterId && !it.isDead }
        ?: charAtLocation
        ?: myCharacters.find { !it.hasActedThisTurn && !it.isDead }
        ?: myCharacters.firstOrNull()
        
    val isMyTurn = gameState.activeTeamTurn == myPlayer?.team
    val isOwner = territoryState.ownerPlayerId == playerId
    
    val ownerPlayer = gameState.players.find { it.id == territoryState.ownerPlayerId }
    val ownerTeam = territoryState.ownerTeam ?: ownerPlayer?.team
    val isTeamOwner = ownerTeam == myPlayer?.team

    var isTavernOpen by remember { mutableStateOf(false) }
    var isInfoOpen by remember(sectorId) { mutableStateOf(false) }
    LaunchedEffect(activeTavernDialogue) {
        if (activeTavernDialogue != null) {
            isTavernOpen = true
        }
    }

    val subLocation = getSubLocationForSector(sectorId)
    val isOwnedByMe = isOwner || isTeamOwner || (myPlayer != null && gameState.teamCastles[myPlayer.team] == sectorId)
    val heroAtSector = myCharacters.find { it.currentSector == sectorId && !it.isDead }
    val commanderForSubLocation = if (activeChar != null && activeChar.currentSector == sectorId && !activeChar.isDead) activeChar else heroAtSector
    val heroPresent = commanderForSubLocation != null
    
    val canAct = isMyTurn && activeChar != null && !activeChar.hasActedThisTurn && !activeChar.isDead
    val hasResources = territoryState.food > 0 || territoryState.gold > 0
    val canCollect = (isOwner || isTeamOwner) && charAtLocation != null && hasResources

    div(className = "territory-modal-backdrop") {
        onClick { onClose() }
        
        div(className = "territory-modal-card glass") {
            // Prevent click inside modal from closing
            onClick { it.stopPropagation() }
            
            div(className = "d-flex justify-between items-center mb-1") {
                div(className = "d-flex items-center gap-1") {
                    if (territoryDef?.isCastle == true) {
                        img(src = "/Castle_icon.png?v=2", alt = "Castle", className = "territory-modal-icon")
                    }
                    div {
                        h3(className = "m-0") {
                            textNode(territoryDef?.name ?: "Sector $sectorId")
                        }
                        span(className = "text-sm text-dark-gray") {
                            textNode("Sector #$sectorId")
                        }
                    }
                }
                div(className = "d-flex items-center gap-05") {
                    button(t("territory.info_btn"), className = "btn btn-xs btn-location-info") {
                        id("location-info-btn-$sectorId")
                        title(t("territory.info_tip"))
                        onClick { isInfoOpen = true }
                    }
                    button("✕", className = "btn-modal-close") {
                        onClick { onClose() }
                    }
                }
            }
            
            // Ownership Status Banner
            val myTeamName = myPlayer?.team?.let { gameState.teamInfos[it]?.name } ?: t("hud.your_team")
            val ownerTeamName = ownerTeam?.let { gameState.teamInfos[it]?.name } ?: t("hud.enemy_team")
            
            val ownerText = when {
                isOwner -> t("territory.owned_you", myTeamName)
                ownerPlayer != null && ownerPlayer.team == myPlayer?.team -> t("territory.owned_ally", ownerPlayer.name)
                ownerPlayer != null -> t("territory.controlled_enemy", ownerPlayer.name, ownerTeamName)
                else -> t("territory.wilderness")
            }
            
            val ownerClass = if (ownerTeam == null) "owner-banner-neutral" else ""
            div(className = "owner-banner $ownerClass mb-1") {
                if (ownerTeam != null) {
                    val teamColor = gameState.teamInfos[ownerTeam]?.color
                    if (teamColor != null) {
                        style("background", "${teamColor}1a") // 10% opacity
                        style("borderLeft", "4px solid $teamColor")
                        style("color", teamColor)
                    }
                }
                span(className = "font-600 text-sm") { textNode(ownerText) }
            }
            
            // Stats Grid
            div(className = "territory-stats-grid mb-1") {
                div(className = "stat-box") {
                    span(className = "stat-label") { textNode(t("territory.cultivation")) }
                    span(className = "stat-value text-primary font-600") { textNode("${territoryState.cultivation}") }
                }
                div(className = "stat-box") {
                    span(className = "stat-label") { textNode(t("territory.protection")) }
                    span(className = "stat-value text-warning font-600") { textNode("${territoryState.protection}") }
                }
                div(className = "stat-box") {
                    span(className = "stat-label") { textNode(t("territory.stored_food")) }
                    span(className = "stat-value font-600") { textNode("${territoryState.food}") }
                }
                div(className = "stat-box") {
                    span(className = "stat-label") { textNode(t("territory.stored_gold")) }
                    span(className = "stat-value font-600") { textNode("${territoryState.gold}") }
                }
            }

            // Castle Lord Audience Section
            val castleLord = getCastleLordForSector(sectorId)
            if (territoryDef?.isCastle == true && castleLord != null) {
                val heroAtCastle = myCharacters.find { it.currentSector == sectorId && !it.isDead }
                val commanderForAudience = if (activeChar != null && activeChar.currentSector == sectorId && !activeChar.isDead) activeChar else heroAtCastle

                div(className = "castle-lord-section glass mb-1") {
                    div(className = "d-flex items-center gap-1 mb-05") {
                        img(src = castleLord.portraitUrl, alt = castleLord.name, className = "castle-lord-avatar")
                        div {
                            div(className = "font-600 text-primary castle-lord-name-title") {
                                textNode(castleLord.name)
                            }
                            div(className = "text-xs text-light-gray font-600") {
                                textNode(castleLord.title)
                            }
                            div(className = "text-xs text-gray font-italic") {
                                textNode(castleLord.faction)
                            }
                        }
                    }
                    div(className = "castle-greeting-banner mb-05") {
                        textNode("\"${castleLord.greeting}\"")
                    }

                    if (commanderForAudience != null) {
                        button(t("castle.seek_audience", castleLord.name), className = "btn btn-sm btn-primary w-full castle-audience-btn") {
                            title(t("castle.seek_audience_tip", castleLord.name))
                            onClick {
                                sendAction(GameAction.ConsultCastleLord(sectorId, commanderForAudience.id))
                            }
                        }
                    } else {
                        div(className = "castle-notice-unoccupied") {
                            span(className = "text-xs text-warning") {
                                textNode(t("castle.station_hero_notice", castleLord.name))
                            }
                        }
                    }
                }
            }

            
            // Actions Section
            if (isOwner || isTeamOwner) {
                h4(className = "m-0 mb-05 text-sm text-gray") { textNode(t("territory.actions_title")) }
                
                div(className = "d-flex flex-col gap-05") {
                    val boostAmount = if (activeChar != null) activeChar.intellect.coerceIn(2, 7) else 2
                    val heroName = activeChar?.name ?: "Hero"
                    div(className = "d-flex gap-1") {
                        button(t("territory.cultivate", boostAmount), className = "btn btn-outline flex-1 ${if (!canAct) "btn-disabled" else ""}") {
                            title(t("territory.cultivate_tip", heroName, boostAmount))
                            onClick {
                                if (canAct && activeChar != null) {
                                    sendAction(GameAction.UpgradeTerritory(sectorId, "CULTIVATION", activeChar.id))
                                }
                            }
                        }
                        button(t("territory.fortify", boostAmount), className = "btn btn-outline flex-1 ${if (!canAct) "btn-disabled" else ""}") {
                            title(t("territory.fortify_tip", heroName, boostAmount))
                            onClick {
                                if (canAct && activeChar != null) {
                                    sendAction(GameAction.UpgradeTerritory(sectorId, "PROTECTION", activeChar.id))
                                }
                            }
                        }
                    }
                    
                    // Search for Scrolls button
                    val canSearch = canAct && activeChar != null && activeChar.currentSector != null &&
                        (activeChar.currentSector == sectorId || isAdjacentSector(activeChar.currentSector!!, sectorId))
                    button(t("territory.search_scrolls"), className = "btn btn-outline ${if (!canSearch) "btn-disabled" else ""}") {
                        title(t("territory.search_scrolls_tip", heroName))
                        onClick {
                            if (canSearch && activeChar != null) {
                                sendAction(GameAction.SearchScroll(sectorId, activeChar.id))
                            }
                        }
                    }
                    
                    val collectTitle = when {
                        charAtLocation == null -> "One of your heroes must be at this location to collect accumulated resources"
                        !hasResources -> "No stored resources available to collect"
                        else -> "Transfers stored Food and Gold to ${charAtLocation.name}'s inventory"
                    }
                    button(
                        if (charAtLocation != null) t("territory.collect_all", territoryState.food, territoryState.gold, charAtLocation.name)
                        else t("territory.collect_need_hero"), 
                        className = "btn btn-primary ${if (!canCollect) "btn-disabled" else ""}"
                    ) {
                        title(collectTitle)
                        onClick {
                            if (canCollect && charAtLocation != null) {
                                sendAction(GameAction.CollectResources(sectorId, charAtLocation.id))
                            }
                        }
                    }
                    
                    if (charAtLocation == null && hasResources) {
                        p(className = "text-xs text-warning m-0 text-center") {
                            textNode(t("territory.move_hero_collect", territoryState.food, territoryState.gold))
                        }
                    }
                    
                    if (!canAct && isMyTurn) {
                        p(className = "text-xs text-red m-0 mt-05 text-center") {
                            textNode(t("territory.already_acted", activeChar?.name ?: "Hero"))
                        }
                    } else if (!isMyTurn) {
                        p(className = "text-xs text-dark-gray m-0 mt-05 text-center") {
                            textNode(t("territory.wait_team_turn"))
                        }
                    }

                    if (subLocation != null) {
                        if (heroPresent && commanderForSubLocation != null) {
                            button("${subLocation.buttonEmoji} ${subLocation.buttonLabel}", className = "btn btn-outline text-gold w-full mt-05") {
                                title(subLocation.buttonTooltip)
                                onClick { isTavernOpen = true }
                            }
                        } else {
                            p(className = "text-xs text-dark-gray m-0 mt-05 text-center") {
                                textNode("Station a commander in ${territoryDef?.name ?: "this sector"} to visit ${subLocation.modalTitle}.")
                            }
                        }
                    }
                }
            } else {
                p(className = "text-sm text-dark-gray text-center m-0 mb-1") {
                    textNode(t("territory.capture_tip"))
                }
                if (subLocation != null) {
                    p(className = "text-xs text-dark-gray mt-1 text-center") {
                        textNode("Control ${territoryDef?.name ?: "this sector"} to access ${subLocation.modalTitle}.")
                    }
                }
            }
            
            // Army Recruitment Section
            if (activeChar != null && !activeChar.isDead) {
                div(className = "recruitment-section mt-1 pt-1") {
                    div(className = "d-flex justify-between items-center mb-05") {
                        h4(className = "m-0 text-sm") { textNode(t("territory.recruit_army_for", activeChar.name)) }
                        span(className = "text-xs text-primary font-600") {
                            textNode("Army: ${activeChar.army.total()}/${models.MAX_SQUAD_SIZE} | Gold: ${activeChar.gold}🪙")
                        }
                    }
                    p(className = "text-xs text-dark-gray m-0 mb-05 text-center") {
                        textNode(t("territory.use_char_panel_recruit"))
                    }
                    
                    if (territoryDef?.isCastle == true) {
                        // Siege Weapon Section
                        div(className = "d-flex justify-between items-center mt-1 mb-05 pt-05 border-t") {
                            h4(className = "m-0 text-sm") { textNode(t("territory.buy_siege")) }
                            span(className = "text-xs text-primary font-600") {
                                textNode(t("territory.siege_count", activeChar.siegeWeapons))
                            }
                        }
                        
                        p(className = "text-xs text-dark-gray m-0 mb-05") {
                            textNode(t("territory.siege_desc"))
                        }
                        
                        val canAffordSiege = activeChar.gold >= 50 && !activeChar.hasActedThisTurn
                        button(t("territory.buy_siege_btn"), className = "btn btn-sm btn-outline w-full ${if (!canAffordSiege) "btn-disabled" else ""}") {
                            title(if (activeChar.hasActedThisTurn) t("territory.siege_acted") else t("territory.siege_buy_tip"))
                            onClick {
                                if (canAffordSiege) {
                                    sendAction(GameAction.BuySiegeWeapon(activeChar.id))
                                }
                            }
                        }
                    }
                }
            }

            // Hunting Grounds & Camp Section
            val huntableHides = HuntingCatalog.huntableHidesIn(sectorId)
            val myCamp = gameState.huntingCamps.find { it.sectorId == sectorId && it.ownerPlayerId == playerId }
            div(className = "hunting-section mt-1 pt-1 border-t") {
                div(className = "d-flex justify-between items-center mb-05") {
                    h4(className = "m-0 text-sm font-600 text-warning") {
                        textNode("🏹 " + t("hunting.title", "Hunting Grounds"))
                    }
                    if (myCamp != null) {
                        span(className = "badge badge-success text-xs") {
                            textNode("⛺ " + t("hunting.camp_active", "Active Camp"))
                        }
                    }
                }

                if (huntableHides.isEmpty()) {
                    p(className = "text-xs text-dark-gray m-0 mb-05 text-center") {
                        textNode(t("hunting.no_game", "No wild beasts roam these grounds."))
                    }
                } else {
                    div(className = "d-flex flex-wrap gap-05 mb-05") {
                        for (hide in huntableHides) {
                            span(className = "badge badge-outline text-xs") {
                                textNode("${hide.emoji} ${hide.displayName}")
                            }
                        }
                    }
                }

                if (charAtLocation != null && isMyTurn) {
                    if (myCamp == null) {
                        if (huntableHides.isNotEmpty()) {
                            val canAffordCamp = charAtLocation.gold >= FurMarket.CAMP_COST
                            button(
                                t("hunting.pitch_camp_btn", FurMarket.CAMP_COST),
                                className = "btn btn-sm btn-outline w-full ${if (!canAffordCamp) "btn-disabled" else ""}"
                            ) {
                                title(if (!canAffordCamp) t("hunting.need_gold", FurMarket.CAMP_COST) else t("hunting.pitch_camp_tip"))
                                onClick {
                                    if (canAffordCamp) {
                                        sendAction(GameAction.PitchHuntingCamp(charAtLocation.id))
                                    }
                                }
                            }
                        }
                    } else {
                        div(className = "d-flex flex-col gap-05") {
                            val canHarvest = !charAtLocation.hasActedThisTurn
                            for (hide in huntableHides) {
                                button(
                                    t("hunting.harvest_btn", hide.emoji, hide.displayName),
                                    className = "btn btn-sm btn-primary w-full ${if (!canHarvest) "btn-disabled" else ""}"
                                ) {
                                    title(if (!canHarvest) t("territory.already_acted", charAtLocation.name) else t("hunting.harvest_tip", hide.displayName))
                                    onClick {
                                        if (canHarvest) {
                                            sendAction(GameAction.HarvestHide(charAtLocation.id, hide))
                                        }
                                    }
                                }
                            }

                            button(
                                t("hunting.dismantle_btn", "Dismantle Camp"),
                                className = "btn btn-sm btn-outline text-red w-full"
                            ) {
                                title(t("hunting.dismantle_tip", "Dismantle this hunting camp"))
                                onClick {
                                    sendAction(GameAction.DismantleHuntingCamp(charAtLocation.id))
                                }
                            }
                        }
                    }
                } else if (huntableHides.isNotEmpty() && charAtLocation == null) {
                    p(className = "text-xs text-dark-gray m-0 text-center font-italic") {
                        textNode(t("hunting.move_hero_notice", "Station a hero here to pitch a camp or harvest pelts."))
                    }
                }
            }
        }

        if (isTavernOpen && commanderForSubLocation != null && subLocation != null) {
            KhorinisTavernModal(
                subLocation = subLocation,
                characterId = commanderForSubLocation.id,
                commanderName = commanderForSubLocation.name,
                activeDialogue = activeTavernDialogue,
                activeHunterIntel = activeHunterIntel,
                onClose = {
                    isTavernOpen = false
                    onDismissTavernDialogue()
                    onDismissHunterIntel()
                },
                onTalk = { patronId ->
                    sendAction(GameAction.TalkToTavernPatron(sectorId, commanderForSubLocation.id, patronId))
                },
                onAskHunter = { patronId, hideType ->
                    sendAction(GameAction.AskHunterAboutHide(sectorId, commanderForSubLocation.id, patronId, hideType))
                }
            )
        }

        if (isInfoOpen) {
            LocationInfoModal(
                sectorId = sectorId,
                fallbackName = territoryDef?.name ?: "Sector $sectorId",
                onClose = { isInfoOpen = false }
            )
        }
    }
}

